// @flow
import * as React from 'react';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import { injectIntl, IntlShape } from 'react-intl';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { withRouter } from 'react-router-dom';
import type { RouterHistory, Match } from 'react-router-dom';
import { connectAPI } from 'speed-js-react';
import { useLeaflet, withLeaflet } from 'react-leaflet';
import DisconnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/DisconnectIcon';
import ConnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/ConnectIcon';
import OpticalSwitchPortRow from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/OpticalSwitchPortRow';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { UUID } from 'esm-web-pro/types/common';
import { ClickedRowContext } from 'esm-web-pro/containers/pages/network/objects/point12/ClickedRowContext';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import { EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { isEmpty } from 'lodash';
import { MapContext } from 'esm-web-pro/containers/map/context';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import sfpMessages from '../../sfp/sfpMessages';

type Props = {
    SFP: Object,
    pointCoords: [number, number],
    onAddSFP: Function,
    onRemoveSFP: Function,
    onPortConnect: Function,
    onPortDisconnect: Function,
    onVisualise?: Function,
    tooltipKey?: string,
    padLevel?: 1 | 2 | 3,
    portPadLevel?: 1 | 2 | 3,
    rowId: string,

    intl: IntlShape,
    history: RouterHistory,
    match: Match,
    removeSFP: Function,
}

const SFPRow = (props: Props) => {
    const {
        SFP, intl: { formatMessage }, padLevel, portPadLevel, onVisualise, rowId, pointCoords,
        history, match, onAddSFP, removeSFP, onRemoveSFP, tooltipKey, onPortConnect, onPortDisconnect,
    } = props;

    const [clickedRowIdentity, setClickedRowIdentity] = React.useContext(ClickedRowContext);
    const [highlightFiberFunction] = useHighlightFiber();
    const editor = React.useContext(EditorContext);
    const map = React.useContext(MapContext);
    const leaflet = useLeaflet();

    const getActions = () => {
        const actions = [];

        if (map.is_draft) {
            if (!SFP) {
                actions.push({
                    key: 'sfp-insert',
                    icon: <ConnectIcon />,
                    size: '20px',
                    width: '3px',
                    callback: onAddSFP,
                    tooltipKey,
                    tooltip: formatMessage(actionMessages.insert),
                });
            } else if (!SFP.built_in) {
                actions.push({
                    key: `${SFP.uuid}-remove`,
                    icon: <DisconnectIcon />,
                    size: '20px',
                    width: '3px',
                    callback: () => {
                        removeSFP(SFP.uuid).then(onRemoveSFP);
                    },
                    tooltipKey,
                    tooltip: formatMessage(actionMessages.remove),
                    disabled: !!SFP.optical_switch_ports.find((port) => port.port),
                });
            }
        }

        if (SFP) {
            actions.push({
                key: `${SFP.uuid}-more`,
                icon: <MeatballsIcon />,
                size: '20px',
                width: '3px',
                callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    const path = match.url.split('/layer');
                    history.push(`${path[0]}/layer/sfp/${SFP.identity}`);
                },
                tooltipKey,
                tooltip: formatMessage(actionMessages.more),
            });
        }

        return actions;
    };

    const getTitle = () => {
        let title = formatMessage(sfpMessages.sfp);

        if (!SFP) {
            title += ` ${formatMessage(sfpMessages.empty)}`;
        } else {
            // eslint-disable-next-line max-len
            title += ` ${formatMessage(SFP.built_in ? sfpMessages.builtin : sfpMessages.taken)}, 
            ${formatMessage(
        SFP.optical_switch_ports.find((port) => port.port) ? sfpMessages.connected : sfpMessages.disconnected,
    )}`;
        }
        return title;
    };

    const opticalSwitchPorts = () => SFP.optical_switch_ports
        .sort((p1, p2) => p1.sequence - p2.sequence).map((port) => (
            <OpticalSwitchPortRow
                key={port.sequence}
                port={port}
                pointCoords={pointCoords}
                onPortConnect={onPortConnect}
                onPortDisconnect={onPortDisconnect}
                tooltipKey={tooltipKey}
                padLevel={portPadLevel}
            />
        ));

    const rowProps = {};
    if (SFP?.optical_switch_ports.find((port) => port.port?.fiber)) {
        if (onVisualise) {
            rowProps.onClick = () => onVisualise(rowId);
        } else {
            rowProps.onClick = () => {
                setClickedRowIdentity(rowId);
                const allCoordinates = [];
                SFP.optical_switch_ports.forEach((opticalSwitchPort) => {
                    if (opticalSwitchPort.port && opticalSwitchPort.port.fiber) {
                        const [callbacks, circuitPath] = highlightFiberCircuit(
                            opticalSwitchPort.circuit_path.path, opticalSwitchPort.port.fiber, leaflet.map,
                            highlightFiberFunction, pointCoords,
                        );
                        allCoordinates.push(...circuitPath);
                        editor.registerMultipleLoadingCallbacks(callbacks);
                        editor.reloadFeatures();
                    }
                });

                if (!isEmpty(allCoordinates)) {
                    leaflet.map.highlightedGeometry = {
                        bounds: allCoordinates, center: pointCoords,
                    };
                    leaflet.map.flyToBoundsCenter(allCoordinates, pointCoords);
                } else {
                    leaflet.map.highlightedGeometry = {
                        bounds: null, center: pointCoords,
                    };
                    leaflet.map.panTo(pointCoords);
                }
            };
        }
    }

    return (
        <>
            <ContentRow
                padLevel={padLevel}
                rowId={rowId}
                clickedRowIdentity={clickedRowIdentity}
                actions={(
                    <RowActions
                        iconPadding="3px"
                        gapBetweenActions="small"
                        actions={getActions()}
                    />
                )}
                {...rowProps}
            >
                <ValueText>
                    {getTitle()}
                </ValueText>
            </ContentRow>
            {SFP && opticalSwitchPorts()}
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    removeSFP: {
        send: (id: UUID) => apiClient.resources.sfp.delete(id),
    },
});

SFPRow.defaultProps = {
    tooltipKey: undefined,
    onVisualise: undefined,
    padLevel: null,
    portPadLevel: null,
};


export default withLeaflet(withRouter(injectIntl(connectAPI(apiCallback)(
    SFPRow,
))));
